import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PasswordField from '../components/PasswordField';
import './Login.css';

function Login() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'register' ? 'register' : 'login');
  const [registrationEnabled, setRegistrationEnabled] = useState(false);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [regUsername,   setRegUsername]   = useState('');
  const [regEmail,      setRegEmail]      = useState('');
  const [regPassword,   setRegPassword]   = useState('');
  const [regAiConsent,  setRegAiConsent]  = useState(false);

  const [verifyStep,  setVerifyStep]  = useState(false);
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyCode,  setVerifyCode]  = useState('');
  const [devCode,     setDevCode]     = useState('');

  const [totpStep,    setTotpStep]    = useState(false);
  const [totpToken,   setTotpToken]   = useState('');
  const [totpCode,    setTotpCode]    = useState('');

  const [notVerifiedEmail, setNotVerifiedEmail] = useState('');
  const [resendSent,       setResendSent]       = useState(false);

  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, loginWith2fa } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccess(location.state.successMessage);
    }
  }, [location.state]);

  useEffect(() => {
    async function checkModules() {
      try {
        const d = await api.getPublicModules();
        setRegistrationEnabled((d.modules || []).includes('user_registration'));
      } catch {
        setRegistrationEnabled(false);
      }
    }
    checkModules();
  }, []);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(username, password);
      if (data.requires_2fa) {
        setTotpToken(data.temp_token);
        setTotpStep(true);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      if (err.code === 'EMAIL_NOT_VERIFIED' || err.message?.includes('EMAIL_NOT_VERIFIED')) {
        setNotVerifiedEmail(err.email || '');
      }
      setError(err.message || 'Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    setResendSent(false);
    setError('');
    try {
      await api.resendVerification(notVerifiedEmail);
      setResendSent(true);
    } catch (err) {
      setError(err.message || 'Fehler beim Senden.');
    }
  }

  async function handleTotp(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginWith2fa(totpToken, totpCode);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || '2FA fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await api.register({
        username: regUsername, password: regPassword, email: regEmail,
      });
      setVerifyEmail(regEmail);
      setDevCode(res.dev_code || '');
      setVerifyStep(true);
    } catch (err) {
      setError(err.message || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.verifyEmail(verifyEmail, verifyCode);
      setSuccess('E-Mail bestätigt! Du kannst dich jetzt anmelden.');
      setVerifyStep(false);
      setTab('login');
      setUsername(regUsername);
    } catch (err) {
      setError(err.message || 'Bestätigung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  function switchTab(t) { setTab(t); setError(''); setSuccess(''); setVerifyStep(false); setTotpStep(false); setNotVerifiedEmail(''); setResendSent(false); }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-title">
            <span className="logo-strix">Strix</span><span className="logo-ly">ly</span>
          </div>
          <p>Dein Kühlschrank. Dein Rezept. Sofort.</p>
        </div>

        {registrationEnabled && !verifyStep && (
          <div className="login-tabs">
            <button className={'login-tab' + (tab === 'login' ? ' active' : '')}
              onClick={() => switchTab('login')} type="button">Anmelden</button>
            <button className={'login-tab' + (tab === 'register' ? ' active' : '')}
              onClick={() => switchTab('register')} type="button">Registrieren</button>
          </div>
        )}

        {error   && <div className="error-message fade-in">{error}</div>}
        {success && <div className="success-message fade-in">{success}</div>}

        {notVerifiedEmail && !resendSent && (
          <div className="verify-info fade-in" style={{ marginBottom: '1rem' }}>
            <p style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>
              Keine E-Mail erhalten?
            </p>
            <button type="button" className="btn btn-secondary btn-sm"
              onClick={handleResendVerification}>
              Code erneut senden
            </button>
          </div>
        )}
        {resendSent && (
          <div className="success-message fade-in">E-Mail wurde gesendet. Bitte prüfe dein Postfach.</div>
        )}

        {totpStep && (
          <form onSubmit={handleTotp} className="login-form">
            <div className="verify-info">
              <div className="verify-icon">🔐</div>
              <h3>Zwei-Faktor-Authentifizierung</h3>
              <p>Gib den 6-stelligen Code aus deiner Authenticator-App ein.</p>
            </div>
            <div className="form-group">
              <label htmlFor="totp-code">Authenticator-Code</label>
              <input id="totp-code" type="text" inputMode="numeric" pattern="[0-9]{6}"
                maxLength={6} value={totpCode} onChange={e => setTotpCode(e.target.value)}
                required autoFocus placeholder="000000" className="verify-code-input" />
            </div>
            <button type="submit" className="btn btn-primary btn-block"
              disabled={loading || totpCode.length !== 6}>
              {loading ? <span className="loader"></span> : 'Bestätigen'}
            </button>
            <button type="button" className="btn-link"
              onClick={() => { setTotpStep(false); setTotpCode(''); setError(''); }}>
              ← Zurück
            </button>
          </form>
        )}

        {!totpStep && !verifyStep && tab === 'login' && (
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Benutzername</label>
              <input id="username" type="text" value={username} onChange={e => setUsername(e.target.value)}
                required autoFocus placeholder="Benutzername" />
            </div>
            <div className="form-group">
              <label htmlFor="password">Passwort</label>
              <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="••••••••" />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? <span className="loader"></span> : 'Anmelden'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              <Link to="/forgot-password" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                Passwort vergessen?
              </Link>
            </div>
          </form>
        )}

        {!totpStep && !verifyStep && tab === 'register' && (
          <form onSubmit={handleRegister} className="login-form">
            <div className="form-group">
              <label htmlFor="reg-username">Benutzername *</label>
              <input id="reg-username" type="text" value={regUsername}
                onChange={e => setRegUsername(e.target.value)} required autoFocus placeholder="mindestens 3 Zeichen" />
            </div>
            <div className="form-group">
              <label htmlFor="reg-email">E-Mail *</label>
              <input id="reg-email" type="email" value={regEmail}
                onChange={e => setRegEmail(e.target.value)} required placeholder="deine@email.at" />
            </div>
            <div className="form-group">
              <label htmlFor="reg-pw">Passwort *</label>
              <PasswordField
                id="reg-pw"
                value={regPassword}
                onChange={e => setRegPassword(e.target.value)}
                placeholder="mindestens 8 Zeichen"
                autoComplete="new-password"
                required
                showStrength
                showGenerator
                onGenerate={pw => setRegPassword(pw)}
              />
            </div>
            <label className="reg-ai-consent">
              <input type="checkbox" checked={regAiConsent}
                onChange={e => setRegAiConsent(e.target.checked)} />
              <span>
                Ich habe zur Kenntnis genommen, dass beim Scannen von Rezepten meine Bilder
                durch <strong>Vertex AI</strong> automatisch analysiert werden.
                Die Verarbeitung erfolgt DSGVO-konform ohne Weitergabe an Dritte.
                <span className="reg-ai-required"> (Pflichtfeld · EU AI Act)</span>
              </span>
            </label>
            <button type="submit" className="btn btn-primary btn-block"
              disabled={loading || !regAiConsent}
              title={!regAiConsent ? 'Bitte den KI-Hinweis bestätigen' : ''}>
              {loading ? <span className="loader"></span> : 'Konto erstellen'}
            </button>
            <p className="reg-hint reg-hint-beta">
              🦉 Du wirst als <strong>Beta-Nutzer</strong> registriert und erhältst kostenlosen
              Zugang zu allen Funktionen während der Beta-Phase. Der Betreiber behält sich vor,
              den Dienst zu einem späteren Zeitpunkt in ein kostenpflichtiges Modell zu überführen
              — registrierte Nutzer werden per E-Mail informiert.
            </p>
            <p className="reg-hint">
              Mit der Registrierung stimmst du unseren{' '}
              <a href="/agb" target="_blank" rel="noreferrer">AGB</a> und{' '}
              <a href="/datenschutz" target="_blank" rel="noreferrer">Datenschutzbestimmungen</a> zu.
            </p>
          </form>
        )}

        {verifyStep && (
          <form onSubmit={handleVerify} className="login-form">
            <div className="verify-info">
              <div className="verify-icon">✉️</div>
              <h3>E-Mail bestätigen</h3>
              <p>Wir haben einen 6-stelligen Code an <strong>{verifyEmail}</strong> gesendet.</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '8px', lineHeight: '1.4' }}>
                E-Mails können je nach Anbieter im Spam-Ordner landen. Bitte prüfe deinen Spam-Ordner und markiere unsere Mails als „Kein Spam".
              </p>
              {devCode && (
                <div className="dev-code-hint">
                  <span>🔧 Testmodus — dein Code:</span>
                  <strong>{devCode}</strong>
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="verify-code">Bestätigungscode</label>
              <input id="verify-code" type="text" inputMode="numeric" pattern="[0-9]{6}"
                maxLength={6} value={verifyCode} onChange={e => setVerifyCode(e.target.value)}
                required autoFocus placeholder="000000" className="verify-code-input" />
            </div>
            <button type="submit" className="btn btn-primary btn-block"
              disabled={loading || verifyCode.length !== 6}>
              {loading ? <span className="loader"></span> : 'Bestätigen'}
            </button>
            <button type="button" className="btn-link"
              onClick={() => { setVerifyStep(false); setError(''); }}>
              ← Zurück zur Registrierung
            </button>
          </form>
        )}

        {!registrationEnabled && (
          <div className="login-footer"><p>Nur für autorisierte Benutzer</p></div>
        )}
      </div>
    </div>
  );
}

export default Login;
